import { useRef } from "react";

import { colorIsLight, ensureLeadingHex } from "Utilities/Colors/colorUtils";
import useIntersectionObserver from "Utilities/Hooks/useIntersectionObserver";

import { useGetHomeSettings, useGetNotesInfinite } from "./queries";

export const useGetBackgroundColorData = () => {
  const { data: homeSettings, ...rest } = useGetHomeSettings();

  const backgroundColor = homeSettings?.backgroundColor
    ? ensureLeadingHex(homeSettings.backgroundColor).toLowerCase()
    : "#153B60";

  const backgroundGradient = homeSettings?.backgroundGradient
    ? homeSettings.backgroundGradient.map((color) =>
        ensureLeadingHex(color).toLowerCase()
      )
    : null;

  const backgroundString = (() => {
    if (backgroundGradient && backgroundGradient.length > 0) {
      return `linear-gradient(135deg, ${backgroundGradient.join(", ")})`;
    }
    return backgroundColor;
  })();

  const backgroundIsLight = colorIsLight(backgroundColor, 30);

  // The backgroundColor, or a fallback to medium/dark gray if the backgroundColor is light
  const accentColor = (() => {
    try {
      if (!backgroundIsLight) return backgroundColor;
      return "#383838";
    } catch (error) {
      return "#383838";
    }
  })();

  // Text color defaults to white, but falls back to medium/dark gray if the backgroundColor is light
  const textColor = (() => {
    if (backgroundIsLight) return "#383838";
    return "#ffffff";
  })();

  return {
    backgroundColor,
    backgroundGradient,
    backgroundString,
    backgroundIsLight,
    accentColor,
    textColor,
    ...rest,
  };
};

export const useGetAccentColorData = () => {
  const { data: homeSettings, ...rest } = useGetHomeSettings();

  const accentColor = homeSettings?.accentColor
    ? ensureLeadingHex(homeSettings.accentColor).toLowerCase()
    : "#153B60";

  return {
    accentColor,
    ...rest,
  };
};

export const useGetSocialLinksData = () => {
  const { data, ...rest } = useGetHomeSettings();

  return {
    socialLinks: data,
    ...rest,
  };
};

export const useGetFontData = () => {
  const { data: homeSettings, ...rest } = useGetHomeSettings();

  const selectedFont = homeSettings?.font || "default";

  return {
    selectedFont,
    ...rest,
  };
};

export const useGetBackgroundPhotoData = () => {
  const { data: homeSettings, ...rest } = useGetHomeSettings();

  const backgroundPhoto = homeSettings?.backgroundPhoto || null;

  return {
    backgroundPhoto,
    ...rest,
  };
};

export const useNotes = (searchTerm) => {
  const loadMoreRef = useRef(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
  } = useGetNotesInfinite({
    searchTerm,
    getNextPageParam: (lastPage) => {
      if (lastPage?.cursor) {
        return lastPage.cursor;
      }
      return undefined;
    },
  });

  const flatNotes = data?.pages.flatMap((page) => page.notes) || [];

  const loadMoreNotes = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  useIntersectionObserver(loadMoreRef, loadMoreNotes);

  return {
    notes: flatNotes,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    loadMoreRef,
    error,
  };
};
